import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherCalendar } from "@subframe/core";
import { format, parse } from "date-fns";
import { Fragment, type ReactNode } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { toast } from "sonner";

import { NumberInput } from "@/components/NumberInput";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  buildEventRecordZodSchema,
  type EventField,
  type EventFieldSchema,
  isLaunchField,
} from "./event-record-schema";

interface EventRecordFormProps {
  schema: EventFieldSchema;
  /** Submit handler — receives the validated data keyed by field key. */
  onSubmit: (data: Record<string, unknown>) => Promise<void> | void;
  isSubmitting?: boolean;
  submitLabel?: string;
  onCancel?: () => void;
  /**
   * `step` (seconds) applied to every `time` input. Omitted by default, so the
   * generic form stays minute-precision; event types that need seconds opt in.
   */
  timeStep?: number;
  /**
   * Per-field-key render overrides. When a field's key has an entry, it renders
   * instead of the generic control — used for bespoke inputs (e.g. fire drill's
   * users-backed "Responsible person" dropdown) without coupling the framework.
   */
  fieldRenderers?: Partial<
    Record<string, (props: FieldControlProps) => ReactNode>
  >;
  /**
   * Render extra, read-only content below the fields from the live form values
   * (e.g. derived timings). Returning `null` renders nothing.
   */
  renderDerived?: (data: Record<string, unknown>) => ReactNode;
  /**
   * Extra content rendered between the fields and the buttons — for inputs that
   * are NOT part of the event type's schema, such as NAU-342's "request
   * acknowledgement from" picker. Deliberately narrow: the owning page holds the
   * state and this framework neither validates nor submits it, so it cannot
   * become a general escape hatch around the schema.
   */
  renderExtra?: () => ReactNode;
}

export function EventRecordForm({
  schema,
  onSubmit,
  isSubmitting,
  submitLabel = "Submit record",
  onCancel,
  timeStep,
  fieldRenderers,
  renderDerived,
  renderExtra,
}: EventRecordFormProps) {
  const zodSchema = buildEventRecordZodSchema(schema);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting: formSubmitting },
  } = useForm<Record<string, unknown>>({
    // The schema is built dynamically, so its inferred input type is `unknown`
    // rather than `FieldValues`; cast as the codebase does for dynamic forms.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(zodSchema as any),
    defaultValues: {},
  });

  const onFormError = (fieldErrors: FieldErrors<Record<string, unknown>>) => {
    const firstKey = Object.keys(fieldErrors)[0];
    if (firstKey) {
      document
        .getElementById(firstKey)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    toast.error("Please fix the errors before submitting");
  };

  const launchFields = schema.fields.filter(isLaunchField);

  if (import.meta.env.DEV) {
    const skipped = schema.fields.filter((f) => !isLaunchField(f));
    if (skipped.length > 0) {
      console.warn(
        `[EventRecordForm] Skipping ${skipped.length} unsupported field type(s):`,
        skipped.map((f) => `${f.key} (${f.type})`),
      );
    }
  }

  const saving = isSubmitting || formSubmitting;

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      className="flex w-full flex-col items-start gap-5"
    >
      {launchFields.map((field) => {
        const controlProps: FieldControlProps = {
          field,
          value: watch(field.key),
          error: errors[field.key]?.message as string | undefined,
          onChange: (v) =>
            setValue(field.key, v, {
              shouldValidate: true,
              shouldDirty: true,
            }),
          timeStep,
        };
        const override = fieldRenderers?.[field.key];
        return (
          <Fragment key={field.key}>
            {override ? (
              override(controlProps)
            ) : (
              <FieldControl {...controlProps} />
            )}
          </Fragment>
        );
      })}

      {renderDerived?.(watch())}

      {renderExtra?.()}

      <div className="flex w-full items-center justify-end gap-2 pt-2">
        {onCancel && (
          <Button
            type="button"
            variant="neutral-secondary"
            size="large"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" size="large" disabled={saving}>
          {saving ? "Submitting…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}

export interface FieldControlProps {
  field: EventField;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
  /** `step` (seconds) for `time` inputs; omitted leaves the browser default. */
  timeStep?: number;
}

function FieldControl({
  field,
  value,
  error,
  onChange,
  timeStep,
}: FieldControlProps) {
  const requiredMark = field.required ? " *" : "";
  const label = `${field.label}${requiredMark}`;

  switch (field.type) {
    case "long_text":
      return (
        <TextArea
          className="h-auto w-full flex-none"
          label={label}
          error={!!error}
          helpText={error}
        >
          <TextArea.Input
            id={field.key}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </TextArea>
      );

    case "numeric":
      return (
        <TextField
          className="h-auto w-full flex-none"
          label={label}
          error={!!error}
          helpText={error}
        >
          <NumberInput
            id={field.key}
            value={typeof value === "number" ? value : null}
            onValueChange={(v) => onChange(v ?? undefined)}
          />
        </TextField>
      );

    case "time":
      return (
        <TextField
          className="h-auto w-full flex-none"
          label={label}
          error={!!error}
          helpText={error}
        >
          <TextField.Input
            id={field.key}
            type="time"
            step={timeStep}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </TextField>
      );

    case "date":
      return (
        <DateField
          field={field}
          label={label}
          value={value as string | undefined}
          error={error}
          onChange={onChange}
        />
      );

    case "yes_no":
      return (
        <Select
          className="h-auto w-full flex-none"
          label={label}
          placeholder="Select…"
          error={!!error}
          helpText={error}
          value={value === undefined ? undefined : value ? "yes" : "no"}
          onValueChange={(v) => onChange(v === "yes")}
        >
          <Select.Item value="yes">Yes</Select.Item>
          <Select.Item value="no">No</Select.Item>
        </Select>
      );

    case "dropdown":
      return (
        <Select
          className="h-auto w-full flex-none"
          label={label}
          placeholder="Select…"
          error={!!error}
          helpText={error}
          value={(value as string) || undefined}
          onValueChange={(v) => onChange(v)}
        >
          {(field.options ?? []).map((opt) => (
            <Select.Item key={opt} value={opt}>
              {opt}
            </Select.Item>
          ))}
        </Select>
      );

    // text (and any launch type not special-cased above)
    default:
      return (
        <TextField
          className="h-auto w-full flex-none"
          label={label}
          error={!!error}
          helpText={error}
        >
          <TextField.Input
            id={field.key}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </TextField>
      );
  }
}

/**
 * Date field — a Calendar in a Popover, storing the value as a `yyyy-MM-dd`
 * string (the shape the backend validator expects). Mirrors the date pattern
 * in ComplianceTaskForm.
 */
function DateField({
  field,
  label,
  value,
  error,
  onChange,
}: {
  field: EventField;
  label: string;
  value: string | undefined;
  error?: string;
  onChange: (value: string) => void;
}) {
  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;

  return (
    <div className="flex w-full flex-col items-start gap-1">
      <span className="text-body-bold font-body-bold text-default-font">
        {label}
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={field.key}
            className={`flex h-10 w-full items-center gap-2 rounded-md border border-solid px-3 text-sm ${
              error ? "border-error-600" : "border-secondary-beige-200"
            } bg-white hover:bg-neutral-50`}
          >
            <FeatherCalendar className="h-4 w-4 text-subtext-color" />
            <span
              className={selected ? "text-neutral-900" : "text-subtext-color"}
            >
              {selected ? format(selected, "d MMM yyyy") : "Select a date"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(date) => {
              if (date) onChange(format(date, "yyyy-MM-dd"));
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {error && (
        <span className="text-caption font-caption text-error-600">
          {error}
        </span>
      )}
    </div>
  );
}
